import React, { useState, useEffect } from "react";
import apiService from "../services/api.service";
import { useAlert } from "../contexts/AlertContext";
import { Modal, Field, TextInput, Select, SearchInput, Toggle, Button, Badge, Icon as OpusIcon } from "./common/opus";
import { toImportCaption } from "../services/scenes/captionPayload";

/**
 * OPUS-907: Cross-environment Scene Import (super admin).
 * OPUS-913: reskinned to the shared design system (opus primitives + 3-step
 * stepper). The orchestration logic is unchanged — the browser holds the
 * source-env session and streams bytes source-S3 -> dest backend -> dest-S3.
 *
 * Props: { open, onClose, organizations (dest orgs), onImported, resumeJob }
 */
const STEPS = ["Source environment", "Pick scene", "Destination + scope"];
// Canonical S3/scene-path segment used across the app: lowercase, spaces -> hyphens.
const pathSeg = (s) => String(s || "").toLowerCase().replace(/\s+/g, "-");

// Known OPUS environments (user + scene API bases). prod has no infix.
const ENVIRONMENTS = [
  { key: "dev", label: "Development", userApi: "https://opus-user-dev.aelive.cloud/v1", sceneApi: "https://opus-scene-dev.aelive.cloud/v1" },
  { key: "stg", label: "Staging", userApi: "https://opus-user-stg.aelive.cloud/v1", sceneApi: "https://opus-scene-stg.aelive.cloud/v1" },
  { key: "tst", label: "Test", userApi: "https://opus-user-tst.aelive.cloud/v1", sceneApi: "https://opus-scene-tst.aelive.cloud/v1" },
  { key: "prod", label: "Production", userApi: "https://opus-user.aelive.cloud/v1", sceneApi: "https://opus-scene.aelive.cloud/v1" },
];

// Exclude the current environment from the import sources.
const _curEnv = (process.env.REACT_APP_ENV || "").toLowerCase();
const CURRENT_ENV_KEY =
  _curEnv.startsWith("dev") ? "dev"
    : (_curEnv.startsWith("stg") || _curEnv.startsWith("stag")) ? "stg"
    : (_curEnv.startsWith("tst") || _curEnv.startsWith("test")) ? "tst"
    : _curEnv.startsWith("prod") ? "prod"
    : null; // local/unknown -> exclude nothing
const AVAILABLE_ENVS = ENVIRONMENTS.filter((e) => e.key !== CURRENT_ENV_KEY);

/* 3-step indicator, ported from the design (modals.jsx ImportStepper). */
function ImportStepper({ step }) {
  // `step` is the live 0-indexed flow step; the transfer step (3) shows all done.
  const current = Math.min(step, 2);
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 22 }}>
      {STEPS.map((label, i) => {
        const active = current === i && step < 3;
        const done = current > i || step >= 3;
        return (
          <React.Fragment key={label}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexShrink: 0 }}>
              <span style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700, flexShrink: 0,
                background: done ? "var(--green)" : active ? "var(--coral)" : "var(--surface-3)", color: done ? "#08140d" : active ? "#fff" : "var(--text-dim)", border: active || done ? "none" : "1px solid var(--border)" }}>
                {done ? <OpusIcon name="check" size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: active ? "var(--text)" : done ? "var(--text-muted)" : "var(--text-dim)", whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 1, background: "var(--border)", margin: "0 14px", minWidth: 16 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* Inline notice banner (error / warning / info) using design tones. */
function Notice({ tone, children }) {
  const map = {
    error: { bg: "rgba(255,69,92,0.10)", border: "rgba(255,69,92,0.28)", color: "var(--coral)", icon: "alertCircle" },
    warning: { bg: "rgba(252,193,68,0.10)", border: "rgba(252,193,68,0.28)", color: "var(--amber)", icon: "alertTriangle" },
    info: { bg: "rgba(88,196,255,0.10)", border: "rgba(88,196,255,0.26)", color: "var(--blue)", icon: "info" },
  }[tone] || {};
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 13px", background: map.bg, border: `1px solid ${map.border}`, borderRadius: "var(--r-md)" }}>
      <OpusIcon name={map.icon} size={16} style={{ color: map.color, flexShrink: 0, marginTop: 1 }} />
      <span style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

export default function SceneImportDialog({ open, onClose, organizations = [], onImported, resumeJob = null }) {
  const alert = useAlert();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  // Source connection
  const [src, setSrc] = useState({ userApi: "", sceneApi: "", username: "", password: "", token: "" });
  const [envKey, setEnvKey] = useState("");
  const [srcOrgs, setSrcOrgs] = useState([]);
  const [srcOrgId, setSrcOrgId] = useState("");
  const [srcScenes, setSrcScenes] = useState([]);
  const [srcSceneId, setSrcSceneId] = useState("");
  const [sceneSearch, setSceneSearch] = useState("");

  // Destination + scope
  const [destOrgId, setDestOrgId] = useState("");
  const [destSceneName, setDestSceneName] = useState("");
  const [includeDb, setIncludeDb] = useState(true);
  const [includeAssets, setIncludeAssets] = useState(true);

  // Transfer
  const [progress, setProgress] = useState(null); // { total, copied, errored, pending }
  const [done, setDone] = useState(false);

  const srcFetch = async (base, path, opts = {}) => {
    // Token can be passed explicitly (used when resuming, before src state settles).
    const token = opts.token || src.token;
    const res = await fetch(`${base}${path}`, {
      ...opts,
      headers: { Accept: "application/json", "Content-Type": "application/json", ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || `${path} failed (${res.status})`);
    return data;
  };

  // Resume mode: prefill the source environment from the job's stored source_env.
  useEffect(() => {
    if (!open || !resumeJob?.source_env) return;
    const env = ENVIRONMENTS.find((e) => e.sceneApi === resumeJob.source_env);
    if (env) { setEnvKey(env.key); setSrc((s) => ({ ...s, userApi: env.userApi, sceneApi: env.sceneApi })); }
    else { setEnvKey("custom"); setSrc((s) => ({ ...s, sceneApi: resumeJob.source_env })); }
    if (resumeJob.progress) setProgress(resumeJob.progress);
  }, [open, resumeJob]);

  // Shared transfer loop: pull pending keys, sign them at source, push to dest.
  const runTransfer = async (jobId, initialPending, base, token) => {
    let pending = (initialPending || []).slice();
    while (pending.length) {
      const batch = pending.slice(0, 5);
      const items = [];
      for (const key of batch) {
        const signed = await srcFetch(base, `/s3/signed-url?key=${encodeURIComponent(key)}`, { token });
        items.push({ source_key: key, signed_url: signed?.data?.url });
      }
      const tr = await apiService.transferSceneImport(jobId, items);
      if (tr?.data?.job?.progress) setProgress(tr.data.job.progress);
      // Re-derive remaining from the server (authoritative + resumable).
      const jr = await apiService.getSceneImportJob(jobId);
      pending = jr?.data?.pending_keys || [];
      if (jr?.data?.job?.progress) setProgress(jr.data.job.progress);
    }
  };

  // Resume an existing job after the user re-connects to its source env.
  const resumeTransfer = async (conn) => {
    setStep(3); setDone(false);
    const jr = await apiService.getSceneImportJob(resumeJob._id);
    if (jr?.data?.job?.progress) setProgress(jr.data.job.progress);
    await runTransfer(resumeJob._id, jr?.data?.pending_keys || [], conn.sceneApi, conn.token);
    setDone(true);
    alert.success("Scene import resumed and completed");
    onImported?.();
  };

  const reset = () => {
    setStep(0); setBusy(false); setError(""); setWarning("");
    setSrc({ userApi: "", sceneApi: "", username: "", password: "", token: "" });
    setEnvKey("");
    setSrcOrgs([]); setSrcOrgId(""); setSrcScenes([]); setSrcSceneId(""); setSceneSearch("");
    setDestOrgId(""); setDestSceneName(""); setIncludeDb(true); setIncludeAssets(true);
    setProgress(null); setDone(false);
  };

  // Step 1 -> 2: carry the source scene's name as the default destination name.
  const goToScope = () => {
    const sc = srcScenes.find((s) => String(s.id || s._id) === String(srcSceneId));
    setDestSceneName((prev) => prev || sc?.name || sc?.scene_name || "");
    setError(""); setWarning("");
    setStep(2);
  };

  const handleClose = () => { if (!busy) { reset(); onClose?.(); } };

  // Step 0 -> connect to source env, load its orgs.
  const connect = async () => {
    setError(""); setBusy(true);
    try {
      const login = await fetch(`${src.userApi.replace(/\/$/, "")}/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: src.username, password: src.password }),
      }).then((r) => r.json());
      const token = login?.data?.accessToken;
      if (!token) throw new Error(login?.message || "Login failed");
      const next = { ...src, userApi: src.userApi.replace(/\/$/, ""), sceneApi: src.sceneApi.replace(/\/$/, ""), token };
      setSrc(next);
      // Resume: skip org/scene picking — go straight back to the transfer loop.
      if (resumeJob) { await resumeTransfer(next); return; }
      const orgsResp = await fetch(`${next.userApi}/common/organization`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json());
      const orgs = Array.isArray(orgsResp) ? orgsResp : (orgsResp?.data || []);
      setSrcOrgs(orgs);
      setStep(1);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  // Step 1 -> when a source org is picked, list its scenes.
  const pickSrcOrg = async (orgId) => {
    setSrcOrgId(orgId); setSrcSceneId(""); setSrcScenes([]); setSceneSearch(""); setError(""); setBusy(true);
    try {
      const resp = await srcFetch(src.sceneApi, `/scenes/organization/${orgId}`);
      const list = resp?.data || resp || [];
      setSrcScenes(Array.isArray(list) ? list : (list.scenes || []));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Step 2 -> run the import.
  const runImport = async () => {
    setError(""); setWarning(""); setBusy(true); setDone(false);
    try {
      const srcOrg = srcOrgs.find((o) => String(o._id) === String(srcOrgId));
      const destOrg = organizations.find((o) => String(o._id) === String(destOrgId));
      const sceneRaw = await srcFetch(src.sceneApi, `/scenes/${srcSceneId}`).then((d) => d?.data || d?.scene || d);
      if (!sceneRaw || (!sceneRaw.scene_name && !sceneRaw.name)) throw new Error("Could not load source scene");
      // Normalize the API's id/name shape to what the import backend expects.
      const scene = {
        scene_name: sceneRaw.scene_name || sceneRaw.name,
        scene_path: sceneRaw.scene_path,
        load_part_path: sceneRaw.load_part_path,
        load_player_path: sceneRaw.load_player_path,
        load_team_badge_path: sceneRaw.load_team_badge_path,
        // OPUS-1391: shared mapper so an imported scene keeps its layers (and
        // disabled flags) instead of silently resetting them in the destination.
        // The source API may name the field `name` on some endpoints.
        captions: (sceneRaw.captions || []).map((c) =>
          toImportCaption({ ...c, caption_name: c.caption_name || c.name })
        ),
      };

      let assetKeys = [];
      let assetsAvailable = includeAssets;
      if (includeAssets) {
        try {
          const keysResp = await srcFetch(src.sceneApi, `/s3/list-all?organizationName=${encodeURIComponent(pathSeg(srcOrg?.name))}&sceneName=${encodeURIComponent(pathSeg(scene.scene_name))}`);
          assetKeys = keysResp?.data?.keys || [];
        } catch (e) {
          // The source env may not expose /s3/list-all (older scene-service). Don't
          // throw away the whole import — fall back to DB-only when that's allowed.
          if (!includeDb) {
            throw new Error(`Couldn't list source files (${e.message}). This source environment may not support file import yet — import the database record only, or pick a newer source environment.`);
          }
          assetsAvailable = false;
          setWarning(`Couldn't list source files (${e.message}). The source environment may not support file import yet — importing the database record only.`);
        }
      }

      const startResp = await apiService.startSceneImport({
        source_env: src.sceneApi,
        source_scene_id: srcSceneId,
        source_org_name: srcOrg?.name,
        dest_org_id: destOrgId,
        dest_org_name: destOrg?.name,
        dest_scene_name: destSceneName,
        include_db: includeDb,
        include_assets: assetsAvailable,
        scene,
        asset_keys: assetKeys,
      });
      const job = startResp?.data?.job;
      if (!job?._id) throw new Error(startResp?.message || "Failed to start import");
      setProgress(job.progress);
      setStep(3);

      if (assetsAvailable) await runTransfer(job._id, assetKeys, src.sceneApi, src.token);
      setDone(true);
      alert.success("Scene import complete");
      onImported?.();
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  const canConnect = src.userApi && src.sceneApi && src.username && src.password;
  const filteredScenes = srcScenes.filter((s) => {
    if (!sceneSearch) return true;
    return String(s.name || s.scene_name || "").toLowerCase().includes(sceneSearch.toLowerCase());
  });

  // Step-appropriate footer actions.
  let footer;
  if (step === 0) footer = (
    <>
      <Button variant="ghost" onClick={handleClose} disabled={busy}>Cancel</Button>
      <Button variant="primary" iconRight="external" onClick={connect} disabled={!canConnect || busy}>{busy ? (resumeJob ? "Reconnecting…" : "Connecting…") : (resumeJob ? "Reconnect & resume" : "Connect")}</Button>
    </>
  );
  else if (step === 1) footer = (
    <>
      <Button variant="ghost" onClick={() => { setError(""); setWarning(""); setStep(0); }} disabled={busy}>Back</Button>
      <Button variant="primary" iconRight="arrowRight" onClick={goToScope} disabled={!srcSceneId || busy}>Next</Button>
    </>
  );
  else if (step === 2) footer = (
    <>
      <Button variant="ghost" onClick={() => { setError(""); setWarning(""); setStep(1); }} disabled={busy}>Back</Button>
      <Button variant="primary" icon="download" onClick={runImport} disabled={!destOrgId || !destSceneName.trim() || (!includeDb && !includeAssets) || busy}>{busy ? "Starting…" : "Start import"}</Button>
    </>
  );
  else footer = <Button variant={done ? "primary" : "ghost"} icon={done ? "check" : undefined} onClick={handleClose} disabled={busy}>{done ? "Done" : "Cancel"}</Button>;

  return (
    <Modal open={open} onClose={handleClose} icon="download" width={600} footer={footer}
      title={resumeJob ? "Resume scene import" : "Import scene from another environment"}
      subtitle={resumeJob ? null : "Copy a scene (and its files) from another OPUS environment into this one."}>
      <ImportStepper step={step} />

      {error && <div style={{ marginBottom: 16 }}><Notice tone="error">{error}</Notice></div>}
      {warning && <div style={{ marginBottom: 16 }}><Notice tone="warning">{warning}</Notice></div>}

      {step === 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {resumeJob && (
            <Notice tone="info">
              Resuming <strong style={{ color: "var(--text)" }}>{resumeJob.scene_name}</strong>
              {resumeJob.progress ? ` — ${resumeJob.progress.copied}/${resumeJob.progress.total} files copied so far` : ""}.
              Re-connect to the source environment to continue the remaining files.
            </Notice>
          )}
          <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.55, margin: 0 }}>
            {resumeJob
              ? "Log in to the source environment again to re-sign the remaining files. Credentials are used only in your browser."
              : "Choose the environment to import from, then log in. Credentials are used only in your browser."}
          </p>
          <Field label="Source environment" required>
            <Select value={envKey} placeholder="Select environment…" icon="layers"
              options={[...AVAILABLE_ENVS.map((e) => ({ value: e.key, label: e.label })), { value: "custom", label: "Custom (enter URLs)" }]}
              onChange={(k) => { setEnvKey(k); const env = ENVIRONMENTS.find((x) => x.key === k); setSrc((s) => ({ ...s, userApi: env?.userApi || "", sceneApi: env?.sceneApi || "" })); }} />
          </Field>
          {envKey === "custom" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Source User API URL"><TextInput value={src.userApi} onChange={(v) => setSrc({ ...src, userApi: v })} placeholder="https://…/v1" /></Field>
              <Field label="Source Scene API URL"><TextInput value={src.sceneApi} onChange={(v) => setSrc({ ...src, sceneApi: v })} placeholder="https://…/v1" /></Field>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="Username" required><TextInput value={src.username} onChange={(v) => setSrc({ ...src, username: v })} placeholder="you@company.com" icon="users" /></Field>
            <Field label="Password" required><TextInput value={src.password} onChange={(v) => setSrc({ ...src, password: v })} placeholder="••••••••" type="password" icon="lock" /></Field>
          </div>
        </div>
      )}

      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Source organisation" required>
            <Select value={srcOrgId} placeholder="Select organisation…" icon="org"
              options={srcOrgs.map((o) => ({ value: o._id, label: o.name }))} onChange={(v) => pickSrcOrg(v)} />
          </Field>
          {srcOrgId && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <SearchInput value={sceneSearch} onChange={setSceneSearch} placeholder="Search scenes…" width={260} />
                <Badge tone="green" dot>Connected{envKey && envKey !== "custom" ? ` · ${(ENVIRONMENTS.find((e) => e.key === envKey) || {}).label}` : ""}</Badge>
              </div>
              <div className="opus-scroll" style={{ border: "1px solid var(--border)", borderRadius: "var(--r-md)", overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
                {busy ? (
                  <div style={{ padding: "28px 14px", textAlign: "center", fontSize: 13, color: "var(--text-dim)" }}>Loading scenes…</div>
                ) : filteredScenes.length ? filteredScenes.map((s, i) => {
                  const id = s.id || s._id;
                  const sel = String(id) === String(srcSceneId);
                  const capCount = Array.isArray(s.captions) ? s.captions.length : null;
                  return (
                    <button key={id} type="button" onClick={() => setSrcSceneId(id)}
                      style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: i < filteredScenes.length - 1 ? "1px solid var(--border)" : "none", cursor: "pointer", border: "none", background: sel ? "rgba(255,69,92,0.08)" : "transparent", boxShadow: sel ? "inset 3px 0 0 var(--coral)" : "none", fontFamily: "var(--font-sans)" }}>
                      <span style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, border: `2px solid ${sel ? "var(--coral)" : "var(--border-strong)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {sel && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--coral)" }} />}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name || s.scene_name}</div>
                        {capCount != null && <div style={{ fontSize: 11.5, color: "var(--text-dim)", marginTop: 2 }}>{capCount} caption{capCount === 1 ? "" : "s"}</div>}
                      </div>
                    </button>
                  );
                }) : <div style={{ padding: "28px 14px", textAlign: "center", fontSize: 13, color: "var(--text-dim)" }}>{sceneSearch ? `No scenes match “${sceneSearch}”.` : "No scenes in this organisation."}</div>}
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Field label="Destination organisation" required>
            <Select value={destOrgId} placeholder="Select organisation…" icon="org"
              options={organizations.map((o) => ({ value: o._id, label: o.name }))} onChange={(v) => setDestOrgId(v)} />
          </Field>
          <Field label="Destination scene name" required helper="Defaults to the source scene name. Change it to import under a new name.">
            <TextInput value={destSceneName} onChange={setDestSceneName} placeholder="e.g. FIFA_CWC_POC_MATCH" icon="scene" />
          </Field>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 10 }}>What to import</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--r-md)", cursor: "pointer" }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Database record</div>
                  <div style={{ fontSize: 12, color: "var(--text-dim)" }}>The scene document and its caption definitions.</div>
                </div>
                <Toggle checked={includeDb} onChange={setIncludeDb} label="Database record" />
              </label>
              <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--r-md)", cursor: "pointer" }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Files (S3 assets)</div>
                  <div style={{ fontSize: 12, color: "var(--text-dim)" }}>Caption media and scene assets, streamed between environments.</div>
                </div>
                <Toggle checked={includeAssets} onChange={setIncludeAssets} label="Files" />
              </label>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 36, height: 36, borderRadius: "var(--r-md)", background: done ? "rgba(69,255,167,0.12)" : "rgba(88,196,255,0.14)", color: done ? "var(--green)" : "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <OpusIcon name={done ? "check" : "download"} size={18} />
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{done ? "Import complete" : "Importing…"}</div>
              <div style={{ fontSize: 12.5, color: "var(--text-dim)" }}>{destSceneName}</div>
            </div>
          </div>
          {progress && (
            <>
              <div style={{ height: 8, borderRadius: 999, background: "var(--surface-3)", overflow: "hidden" }}>
                <div style={{ width: `${progress.total ? Math.round((progress.copied / progress.total) * 100) : 100}%`, height: "100%", borderRadius: 999, background: progress.errored ? "var(--amber)" : "var(--green)", transition: "width .3s ease" }} />
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                {includeDb ? "Database record copied. " : ""}
                {progress.total
                  ? `Assets: ${progress.copied}/${progress.total} copied${progress.errored ? `, ${progress.errored} failed` : ""}.`
                  : "No assets to copy."}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
